import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { CheckCircle, ShieldCheck, MapPin } from 'lucide-react';
import { PublicHeader } from '../components/PublicHeader';
import { HeadlessBooker } from '../components/booking/HeadlessBooker';
import { useForceLightMode } from '../hooks/useForceLightMode';

interface LeadDetails {
  name: string;
  email: string;
  organisation: string;
  role: string;
  message: string;
}

const trackEvent = (event: string) => {
  if (typeof window !== 'undefined' && typeof window.plausible === 'function') {
    window.plausible(event);
  }
};

export const BookDemoSchedulePage: React.FC = () => {
  useForceLightMode();
  const location = useLocation();
  const lead = location.state as LeadDetails | null;

  if (!lead) {
    return <Navigate to="/demo" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Confirmation + summary */}
          <div>
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700 mb-6">
              <CheckCircle className="h-3.5 w-3.5" />
              <span>Details received</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
              Pick a time that works for you
            </h1>
            <p className="text-xl text-medical-600 mb-10 leading-relaxed font-light">
              Choose any 30-minute slot below. You'll receive a calendar invite with a video-call link straight after.
            </p>

            <div className="bg-white rounded-xl border border-medical-200/70 p-6 mb-8">
              <p className="text-xs font-semibold uppercase tracking-wide text-medical-500 mb-3">Your details</p>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-medical-500">Name</dt>
                  <dd className="text-medical-900 font-medium text-right">{lead.name}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-medical-500">Email</dt>
                  <dd className="text-medical-900 font-medium text-right break-all">{lead.email}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-medical-500">Organisation</dt>
                  <dd className="text-medical-900 font-medium text-right">{lead.organisation}</dd>
                </div>
                {lead.role && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-medical-500">Role</dt>
                    <dd className="text-medical-900 font-medium text-right capitalize">{lead.role.replace('-', ' ')}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-4 text-xs text-medical-500">
                Need to change something?{' '}
                <Link to="/demo" className="text-primary-600 hover:underline">Edit details</Link>
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {[
                { icon: ShieldCheck, label: 'NHS DSPT Compliant' },
                { icon: MapPin, label: 'UK Data Residency' },
              ].map((badge, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-medium text-green-700"
                >
                  <badge.icon className="h-3 w-3" />
                  <span>{badge.label}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Right: headless booking widget */}
          <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 overflow-hidden">
            <HeadlessBooker
              lead={lead}
              onLoaded={() => trackEvent('demo_calendar_loaded')}
              onBooked={() => trackEvent('demo_booking_completed')}
            />
          </div>
        </div>
      </div>

      <footer className="border-t border-medical-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis™. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
          </p>
        </div>
      </footer>
    </div>
  );
};
